import { OKLAB_MIX_GLSL } from './oklab.js';

// Stargate-style wormhole ride interior. Rendered on an inside-facing
// sphere that follows the camera; everything is generated from the
// camera-local ray direction, so there is no UV seam and no
// dependence on world scale (the same shader works in Sol space and
// Gargantua space).
export const wormholeTunnelVertexShader = `
  varying vec3 vRayDir;

  void main() {
    vRayDir = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const wormholeTunnelFragmentShader = `
  ${OKLAB_MIX_GLSL}
  precision highp float;

  varying vec3 vRayDir;

  uniform float uTime;     // seconds since flight start
  uniform float uTravel;   // 0..1 progress through the tunnel
  uniform float uFlash;    // event-horizon flash, 0..1
  uniform float uAlpha;    // shell opacity (1 in flight, fades on exit tail)

  const float PI = 3.14159265358979;

  // Strict SG-1 palette — blue family only, no rainbow, no orange.
  const vec3 NAVY = vec3(0.024, 0.067, 0.173);  // #06112c deep wall base
  const vec3 BLUE = vec3(0.165, 0.420, 1.000);  // #2a6bff electric mid-tone
  const vec3 CYAN = vec3(0.875, 0.980, 1.000);  // #dffaff crest highlight

  float hash31(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
  }

  float noise3(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    vec3 u = f * f * (3.0 - 2.0 * f);
    float n000 = hash31(i);
    float n100 = hash31(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash31(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash31(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash31(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash31(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash31(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash31(i + vec3(1.0, 1.0, 1.0));
    return mix(
      mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y),
      mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y),
      u.z
    );
  }

  float fbm(vec3 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * noise3(p);
      p = p * 2.07 + vec3(11.3, 7.7, 5.1);
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec3 dir = normalize(vRayDir);

    // ---- S-curve bends + camera bank --------------------------------
    // Two sway layers move the vanishing point: the original
    // incommensurate sines plus a second, longer-wavelength snake.
    // Their phases rarely zero out together, so at most moments the
    // far center is occluded by the winding wall instead of opening
    // onto the vanishing point — the SG-1 look where the meandering
    // passage itself blocks the view ahead. Weighting the sway by
    // forwardness bends the tunnel AHEAD of the camera instead of
    // shifting the whole view, and a synced roll around the flight
    // axis banks the horizon into each bend — the body-feel of
    // steering through the curves.
    float bendX = sin(uTime * 0.45 + uTravel * 5.0);
    float bendY = sin(uTime * 0.31 + uTravel * 3.4 + 1.7) * 0.6;
    float snakeX = sin(uTime * 0.19 + uTravel * 2.1 + 4.2);
    float snakeY = cos(uTime * 0.13 + uTravel * 1.5 + 2.3) * 0.7;
    vec2 sway = vec2(bendX, bendY) * 0.22 + vec2(snakeX, snakeY) * 0.20;
    float bank = -sway.x * 0.55;
    float cb = cos(bank);
    float sb = sin(bank);
    dir.xy = mat2(cb, -sb, sb, cb) * dir.xy;

    float forward0 = clamp(-dir.z, -1.0, 1.0);
    dir.xy += sway * smoothstep(0.0, 1.0, forward0);
    dir = normalize(dir);

    // ---- Cylinder coordinates ----------------------------------------
    // sinR is the perpendicular component of the ray; axial is the
    // signed distance along the tube axis (in wall-radius units) where
    // the ray meets the wall, dist the slant range to that hit point.
    float forward = clamp(-dir.z, -1.0, 1.0);
    float theta = atan(dir.y, dir.x) + uTime * 0.14;
    float sinR = max(length(dir.xy), 0.04);
    float axial = -dir.z / sinR;
    float dist = 1.0 / sinR;

    // Wall flow — features rush TOWARD the camera as flow increases,
    // with a quadratic travel term so the ride accelerates.
    float flow = uTime * 2.2 + uTravel * uTravel * 12.0;

    // ---- Vortex swirl --------------------------------------------------
    // The walls rotate slowly around the flight axis (whirlpool view),
    // and the rotation advances with axial depth, so the shear turns
    // axially-stretched noise features into filaments that spiral
    // inward toward the dark center. uTime * 0.55 on top of the 0.14
    // base drift gives one full visible rotation roughly every 9 s —
    // slow and majestic, not a blender.
    float thetaSwirl = theta + axial * 0.38 + uTime * 0.55;

    // ---- Watery cloud walls (domain-warped FBM) ----------------------
    // The swirled theta is embedded on a circle (cos/sin) so the noise
    // wraps seamlessly around the tube. The axial frequency is kept low
    // (0.32 vs the 2.2 circumferential radius) so features elongate
    // along the tube; the depth-dependent twist above then stretches
    // them along the spiral direction into streaky swirl filaments.
    vec3 p = vec3(cos(thetaSwirl) * 2.2, sin(thetaSwirl) * 2.2, axial * 0.32 + flow);
    float q = fbm(p * 1.15 + vec3(0.0, 0.0, flow * 0.35));
    float n = fbm(p + vec3(q * 1.5, q * 1.1, q * 0.8));
    // Refractive shimmer — a fine, faster-moving second sampling that
    // wobbles the crests like light through moving water.
    float shimmer = noise3(p * 4.3 + vec3(0.0, 0.0, flow * 1.8));

    // Nearby walls read bright; distance fogs toward the deep haze.
    float wallFade = exp(-(dist - 1.0) * 0.14);

    // ---- Blue gradient stack (OKLCh, holds chroma through the arc) ---
    vec3 col = oklchMix(NAVY, BLUE, smoothstep(0.22, 0.85, n) * (0.35 + 0.55 * wallFade));
    float crest = smoothstep(0.60, 0.95, n + shimmer * 0.22) * wallFade;
    col = oklchMix(col, CYAN, crest * 0.75);
    // Crest sparkle — push the very top of the watery crests past the
    // bloom threshold (0.92) so the near walls keep their glints now
    // that no far-end glow feeds the bloom pass.
    col += CYAN * pow(crest, 3.0) * 0.4;

    // ---- Energy ripples billowing past --------------------------------
    // Noise-phased rings, so they read as soft luminous billows rather
    // than geometric hoops; the +flow phase moves them past the camera.
    float band = sin(axial * 1.9 + flow * 1.6 + (q - 0.5) * 6.0);
    float ripple = pow(clamp(band * 0.5 + 0.5, 0.0, 1.0), 6.0);
    col += oklchMix(BLUE, CYAN, 0.7) * ripple * wallFade * (0.5 + 0.4 * sin(uTravel * PI));

    // ---- Depth haze ----------------------------------------------------
    // No light at the end of this tunnel. Like the SG-1 interior, the
    // far end recedes into deep navy darkness: slant-range fog sinks
    // distant walls toward near-black blue, and the vanishing point
    // itself gets an extra occlusion drop. Depth reads from fog and
    // the winding walls blocking the view, not from a glow.
    vec3 hazeCol = NAVY * 0.22;  // deepest blue, almost black
    float haze = 1.0 - exp(-max(dist - 1.0, 0.0) * 0.16);
    col = mix(col, hazeCol, haze);
    col = mix(col, hazeCol * 0.5, smoothstep(0.965, 0.999, forward));

    // ---- Luminous rim around the void ---------------------------------
    // In the reference frame the brightest light hugs the dark center:
    // an electric-blue annulus just outside the far-end haze, falling
    // off outward, while the void itself stays dark. Peaks at
    // forward ~= 0.965, right before the center occlusion above takes
    // over. Cloud noise breaks the ring so it never reads geometric,
    // and the peak kisses but never crosses the 0.92 bloom threshold —
    // no white blob.
    float rim = smoothstep(0.86, 0.965, forward)
              * (1.0 - smoothstep(0.965, 0.995, forward));
    vec3 rimCol = oklchMix(BLUE, vec3(0.624, 0.847, 1.0), 0.55); // #2a6bff -> #9fd8ff
    col += rimCol * rim * (0.7 + 0.45 * n) * (0.55 + 0.18 * shimmer);

    // Looking back: dark navy, not black, not noisy.
    col *= mix(0.3, 1.0, smoothstep(-0.45, 0.3, forward));

    // ---- Event-horizon flash -------------------------------------------
    // Engulfing white-cyan on puddle entry and exit; > 1.0 so the brief
    // flash blooms.
    vec3 flashCol = oklchMix(CYAN, vec3(1.0), 0.55) * 1.25;
    col = mix(col, flashCol, clamp(uFlash, 0.0, 1.0));

    gl_FragColor = vec4(col, uAlpha);
  }
`;

// Interstellar's wormhole seen from outside: a spherical gravitational
// lens with no surface (James, von Tunzelmann, Franklin & Thorne 2015,
// "Visualizing Interstellar's Wormhole", Am. J. Phys. 83, 486). The mesh is
// an inside-facing sphere many throat radii across, so its fragments are
// simply the camera rays that pass near the mouth. Each ray's fate comes
// from a table of the Dneg metric's null geodesics (src/wormholeLens.js):
// which side of the wormhole it ends on and the angle it has turned
// through. Rays inside the critical impact parameter (b < rho) go through
// and show the other galaxy; the rest are bent around the mouth and show
// our own sky, lensed into an Einstein ring that dies away with distance.
//
// All the geometry runs in view space (camera at the origin), so the
// wormhole's large world coordinates never meet float32.
export const wormholeLensVertexShader = `
  varying vec3 vViewPosition;

  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vViewPosition = mv.xyz;
    gl_Position = projectionMatrix * mv;
  }
`;

// LENS_* constants arrive as material defines from src/wormholeLens.js and
// must match the table's row and column mappings there.
export const wormholeLensFragmentShader = `
  precision highp float;
  precision highp sampler2D;

  uniform sampler2D uTable;       // LENS_COLS x (LENS_ROWS + 1): T(d, b) - acos(b/d) rows, then K(b)
  uniform samplerCube uLocalSky;  // our side, rendered from the mouth
  uniform sampler2D uFarSky;      // the other galaxy, equirectangular
  uniform mat3 uFarFrame;         // world direction -> the other galaxy's frame
  uniform vec3 uCenterView;       // wormhole centre in view space
  uniform float uRho;             // throat radius, world units
  uniform float uMeshRadius;      // lens extent, throat radii
  uniform float uFarGain;
  uniform float uFade;

  varying vec3 vViewPosition;

  const float PI = 3.141592653589793;

  float lensTexel(int x, int y) { return texelFetch(uTable, ivec2(x, y), 0).r; }

  float rowCoord(float d) {
    return log(1.0 + (d - 1.0) / LENS_H0) / LENS_LROW * float(LENS_ROWS - 1);
  }
  // asinh written out in its stable form: log(x + sqrt(x^2 + 1)) cancels
  // for large negative x (b near 0 is x = -250), which stair-stepped phi
  // and smeared the middle of the mouth into radial streaks.
  float asinhStable(float x) {
    return sign(x) * log(abs(x) + sqrt(x * x + 1.0));
  }
  float colCoord(float b) {
    return (asinhStable((b - 1.0) / LENS_BW) - LENS_U0) / (LENS_U1 - LENS_U0) * float(LENS_COLS - 1);
  }

  // T(d, b) - acos(b / d): what the bending adds to the angle a ray turns
  // between its turning point (or the throat) and the camera. Read
  // bilinearly by hand: the table is float.
  float tableT(float fx, float fy) {
    fx = clamp(fx, 0.0, float(LENS_COLS - 1));
    fy = clamp(fy, 0.0, float(LENS_ROWS - 1));
    int x0 = int(floor(fx));
    int y0 = int(floor(fy));
    int x1 = min(x0 + 1, LENS_COLS - 1);
    int y1 = min(y0 + 1, LENS_ROWS - 1);
    float tx = fx - float(x0);
    float ty = fy - float(y0);
    float r0 = mix(lensTexel(x0, y0), lensTexel(x1, y0), tx);
    float r1 = mix(lensTexel(x0, y1), lensTexel(x1, y1), tx);
    return mix(r0, r1, ty);
  }
  // K(b): the angle it turns from there out to infinity.
  float tableK(float fx) {
    fx = clamp(fx, 0.0, float(LENS_COLS - 1));
    int x0 = int(floor(fx));
    int x1 = min(x0 + 1, LENS_COLS - 1);
    return mix(lensTexel(x0, LENS_ROWS), lensTexel(x1, LENS_ROWS), fx - float(x0));
  }

  void main() {
    // The ray and the camera's place, in throat radii about the mouth.
    vec3 v = normalize(vViewPosition);
    vec3 rel = -uCenterView / uRho;
    float dist = length(rel);
    vec3 er = rel / max(dist, 1e-6);
    float d = max(dist, 1.0002);
    float vr = dot(v, er);                       // < 0: heading toward the mouth
    vec3 vp = v - vr * er;
    float s = length(vp);
    vec3 ep = s > 1e-6 ? vp / s : normalize(cross(er, abs(er.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0)));
    float b = d * s;                             // impact parameter
    // phi: the ray's angle at infinity, measured from er in the plane of
    // er and the ray. Unbent, it is just the ray's own angle from er.
    float straight = atan(s, vr);
    float phi = straight;
    float far = 0.0;
    if (b < LENS_B_MAX) {
      float fx = colCoord(b);
      float K = tableK(fx);
      // Beyond the last row the ray crosses flat space, which adds nothing
      // to the bending: the last row's residual holds.
      float T = tableT(fx, rowCoord(min(d, LENS_D_MAX))) + acos(clamp(b / d, 0.0, 1.0));
      float bent = vr < 0.0 ? T + K : K - T;
      far = (vr < 0.0 && b < 1.0) ? 1.0 : 0.0;
      // The bending of our own sky fades out before the mesh ends, so the
      // lens has no edge.
      float w = far > 0.5 ? 1.0 : 1.0 - smoothstep(0.45 * uMeshRadius, 0.85 * uMeshRadius, b);
      phi = mix(straight, bent, w);
    }
    vec3 dirView = cos(phi) * er + sin(phi) * ep;
    vec3 dir = normalize((vec4(dirView, 0.0) * viewMatrix).xyz);   // to world

    // Both skies are read every pixel with explicit gradients: derivatives
    // must not be taken inside the branch that picks a side.
    vec3 gx = dFdx(dir);
    vec3 gy = dFdy(dir);
    vec3 local = textureGrad(uLocalSky, dir, gx, gy).rgb;

    // The far side, point-reflected so looking into the mouth looks ahead
    // into the other galaxy.
    vec3 fdir = uFarFrame * (-dir);
    vec2 uv = vec2(atan(fdir.z, fdir.x) / (2.0 * PI) + 0.5, asin(clamp(fdir.y, -1.0, 1.0)) / PI + 0.5);
    vec2 ux = dFdx(uv);
    vec2 uy = dFdy(uv);
    ux.x -= floor(ux.x + 0.5);
    uy.x -= floor(uy.x + 0.5);
    vec3 farColor = textureGrad(uFarSky, uv, ux, uy).rgb * uFarGain;

    vec3 color = mix(local, farColor, far);
    float alpha = (1.0 - smoothstep(0.85 * uMeshRadius, uMeshRadius, b)) * uFade;
    gl_FragColor = vec4(color, alpha);
  }
`;

// The galaxy on the far side of the wormhole, painted once into an
// equirectangular target (src/wormholeLens.js) a strip at a time. Per the
// paper the far mouth sits nearer its galaxy's centre than we sit to ours:
// larger and brighter nebulae, more dense dust, more and brighter stars.
// Wide cool haze, big nebula complexes with cream and pink cores, a warm
// textured band, dark dust silhouettes, small warm knots and a fine grain
// of stars. Galactic frame: the band lies along y = 0.
export const farGalaxyVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

export const farGalaxyFragmentShader = `
  ${OKLAB_MIX_GLSL}
  precision highp float;

  uniform vec2 uSize;
  varying vec2 vUv;

  const float PI = 3.141592653589793;

  float hash13(vec3 p3) {
    p3 = fract(p3 * 0.1031);
    p3 += dot(p3, p3.zyx + 31.32);
    return fract((p3.x + p3.y) * p3.z);
  }
  vec3 hash33(vec3 p3) {
    p3 = fract(p3 * vec3(0.1031, 0.1030, 0.0973));
    p3 += dot(p3, p3.yxz + 33.33);
    return fract((p3.xxy + p3.yxx) * p3.zyx);
  }

  float vnoise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    vec3 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(mix(hash13(i), hash13(i + vec3(1.0, 0.0, 0.0)), u.x),
          mix(hash13(i + vec3(0.0, 1.0, 0.0)), hash13(i + vec3(1.0, 1.0, 0.0)), u.x), u.y),
      mix(mix(hash13(i + vec3(0.0, 0.0, 1.0)), hash13(i + vec3(1.0, 0.0, 1.0)), u.x),
          mix(hash13(i + vec3(0.0, 1.0, 1.0)), hash13(i + vec3(1.0, 1.0, 1.0)), u.x), u.y),
      u.z);
  }
  float fbm(vec3 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 6; i++) {
      v += a * vnoise(p);
      p = p * 2.03 + vec3(17.1, 9.2, 5.3);
      a *= 0.5;
    }
    return v;
  }

  // Blackbody-ish star tints, from hot blue-white to cool orange.
  vec3 starTint(float t) {
    vec3 hot = vec3(0.70, 0.80, 1.00);
    vec3 sun = vec3(1.00, 0.94, 0.84);
    vec3 cool = vec3(1.00, 0.66, 0.40);
    return t < 0.5 ? oklchMix(hot, sun, t * 2.0) : oklchMix(sun, cool, (t - 0.5) * 2.0);
  }

  // One star layer: a star per cell of a direction-space grid (scale S)
  // with probability p, a Gaussian of angular width sigma, brightness
  // drawn from a power law (index k) up to a cap.
  vec3 starLayer(vec3 dir, float S, float p, float sigma, float gain, float k, float cap, float seed) {
    vec3 cell = floor(dir * S);
    vec3 sum = vec3(0.0);
    for (int z = -1; z <= 1; z++)
    for (int y = -1; y <= 1; y++)
    for (int x = -1; x <= 1; x++) {
      vec3 c = cell + vec3(float(x), float(y), float(z));
      vec3 h = hash33(c + seed);
      if (h.x > p) continue;
      vec3 sp = normalize((c + hash33(c + seed + 7.7)) / S);
      vec3 dd = dir - sp;
      float a2 = dot(dd, dd);
      float lum = min(gain * pow(max(h.y, 1e-4), -k), cap);
      sum += starTint(h.z) * lum * exp(-a2 / (2.0 * sigma * sigma));
    }
    return sum;
  }

  // Small bright knots: compact nebulae and distant galaxies, warm, with
  // whiter cores, a few degrees apart.
  vec3 knots(vec3 dir, float texel) {
    float S = 1.0 / (texel * 26.0);
    vec3 cell = floor(dir * S);
    vec3 sum = vec3(0.0);
    for (int z = -1; z <= 1; z++)
    for (int y = -1; y <= 1; y++)
    for (int x = -1; x <= 1; x++) {
      vec3 c = cell + vec3(float(x), float(y), float(z));
      vec3 h = hash33(c + 71.3);
      if (h.x > 0.025) continue;
      vec3 kp = normalize((c + hash33(c + 5.9)) / S);
      vec3 dd = dir - kp;
      float size = texel * mix(1.2, 3.8, h.y * h.y);
      float a2 = dot(dd, dd) / (size * size);
      float lumpy = 0.6 + 0.8 * vnoise(dir / size * 0.7 + c);
      vec3 warm = oklchMix(vec3(1.00, 0.58, 0.40), vec3(1.00, 0.80, 0.70), h.z);
      sum += warm * exp(-a2 * 0.5) * lumpy * mix(0.18, 0.8, h.z) + vec3(1.0, 0.95, 0.9) * exp(-a2 * 2.5) * 0.45 * h.z;
    }
    return sum;
  }

  void main() {
    float lon = (vUv.x - 0.5) * 2.0 * PI;
    float lat = (vUv.y - 0.5) * PI;
    vec3 dir = vec3(cos(lat) * cos(lon), sin(lat), cos(lat) * sin(lon));
    float texel = PI / uSize.y;
    float glat = asin(clamp(dir.y, -1.0, 1.0));

    // A large-scale warp shared by every cloud layer, so haze, nebulae and
    // dust bend together.
    vec3 wq = vec3(fbm(dir * 1.7 + 1.0), fbm(dir * 1.7 + 4.0), fbm(dir * 1.7 + 8.0));

    // Cool blue-grey haze across the whole sky.
    float haze = smoothstep(0.40, 0.85, fbm(dir * 2.4 + wq * 1.7));
    vec3 col = vec3(0.46, 0.54, 0.70) * haze * 0.11;

    // Nebula complexes: where a low-frequency field allows, warped clouds
    // with bright cores; grey-blue at the rims, cream in the cores, pink
    // and brown in places.
    float region = smoothstep(0.42, 0.68, fbm(dir * 1.15 + wq * 0.9 + 21.0));
    float cloud = fbm(dir * 3.6 + wq * 2.3 + 13.0);
    float body = smoothstep(0.38, 0.80, cloud) * region;
    float core = pow(smoothstep(0.55, 0.95, cloud), 2.0) * region;
    float warm = smoothstep(0.40, 0.72, fbm(dir * 2.1 + 31.0));
    vec3 rimCol = vec3(0.52, 0.60, 0.80);
    vec3 creamCol = vec3(1.00, 0.93, 0.82);
    vec3 pinkCol = vec3(0.92, 0.60, 0.52);
    vec3 nebCol = oklchMix(rimCol, creamCol, smoothstep(0.0, 0.7, core / max(body, 1e-3)));
    nebCol = oklchMix(nebCol, pinkCol, warm * 0.55);
    col += nebCol * (body * 0.24 + core * 1.3);

    // The band: broad, warm and textured, brighter toward its centre (+x).
    float thick = 0.11 + 0.09 * fbm(dir * 1.4 + 41.0);
    float band = exp(-(glat * glat) / (thick * thick));
    float centre = exp(-pow(acos(clamp(dir.x, -1.0, 1.0)), 2.0) / 0.9);
    float bandTex = 0.35 + 1.3 * fbm(dir * 6.0 + wq * 1.6 + 51.0);
    vec3 bandCol = oklchMix(vec3(1.00, 0.88, 0.74), vec3(0.94, 0.68, 0.60), fbm(dir * 2.8 + 61.0));
    col += bandCol * band * bandTex * (0.14 + 0.40 * centre);

    // Dust: dark ridged lanes, densest over the band and the nebulae, which
    // redden what light gets through.
    vec3 w = vec3(fbm(dir * 3.0 + 1.3), fbm(dir * 3.0 + 6.1), fbm(dir * 3.0 + 2.7));
    float ridge = 1.0 - abs(2.0 * fbm(dir * 5.2 + w * 2.0 + wq) - 1.0);
    float tau = pow(ridge, 5.0) * 4.5 * (band + region * 0.9) + 0.22 * band;
    vec3 trans = exp(-tau * vec3(0.80, 1.0, 1.30));
    col *= trans;

    col += knots(dir, texel) * mix(vec3(1.0), trans, 0.6);

    // Stars: a fine dense grain and a sparser set of bright ones, more of
    // both in the band and the nebulae; only the rare brightest pass the
    // bloom threshold.
    float density = 0.55 + 1.5 * band + 1.2 * body;
    vec3 stars = starLayer(dir, 1.0 / (texel * 0.9), 0.07 * density, texel * 0.42, 0.018, 0.8, 1.2, 0.0);
    stars += starLayer(dir, 1.0 / (texel * 8.0), 0.10 * min(density, 2.0), texel * 0.6, 0.10, 1.0, 8.0, 19.0);
    col += stars * mix(vec3(1.0), trans, 0.8);

    gl_FragColor = vec4(col, 1.0);
  }
`;
